import moment from 'moment';

export type TimeOffPerson = {
	id?: number | string;
	firstName?: string | null;
	lastName?: string | null;
} | null;

export type UserTimeOffMatch = {
	id?: number | string;
	['@id']?: string;
	startAt?: string;
	endAt?: string;
};

export type TimeOffCalendarRequest = {
	id?: number;
	startAt?: string | null;
	endAt?: string | null;
	employeeRequest?: TimeOffPerson;
	createdBy?: TimeOffPerson;
	approvedBy?: TimeOffPerson;
	updatedBy?: TimeOffPerson;
	updated_at?: string | null;
	updatedAt?: string | null;
	userTimeOff?: UserTimeOffMatch | string | number | null;
};

export type TimeOffCalendarEvent = {
	isTimeOff?: boolean;
	employee?: TimeOffPerson;
	startAt?: string | Date | null;
	endAt?: string | Date | null;
	userTimeOffId?: number | string | null;
	timeOffRequest?: TimeOffCalendarRequest | null;
};

const asTimeOffCalendarEvent = (
	event?: unknown
): TimeOffCalendarEvent | null => {
	if (!event || typeof event !== 'object') return null;
	return event as TimeOffCalendarEvent;
};

export const formatTimeOffPersonName = (person?: TimeOffPerson): string => {
	if (!person) return '';
	return `${person.firstName || ''} ${person.lastName || ''}`.trim();
};

export const getTimeOffRequestFromEvent = (
	event?: unknown
): TimeOffCalendarRequest | null => asTimeOffCalendarEvent(event)?.timeOffRequest || null;

const parseNumericId = (value: unknown): number | null => {
	if (value == null || value === '') return null;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') {
		const match = value.trim().match(/(\d+)$/);
		if (match) return Number(match[1]);
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		if (record.id != null) return parseNumericId(record.id);
		if (typeof record['@id'] === 'string') return parseNumericId(record['@id']);
	}
	return null;
};

const toComparableTimeOffInstant = (value?: string | Date | null): string | null => {
	if (!value) return null;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : null;
};

export const parseUserTimeOffId = (value: unknown): number | null => parseNumericId(value);

export const findMatchingUserTimeOffId = (
	userTimeOffs: UserTimeOffMatch[] | undefined,
	startAt?: string | Date | null,
	endAt?: string | Date | null
): number | null => {
	if (!userTimeOffs?.length || !startAt || !endAt) return null;

	const start = toComparableTimeOffInstant(startAt);
	const end = toComparableTimeOffInstant(endAt);
	if (!start || !end) return null;

	const exactMatch = userTimeOffs.find(
		(timeOff) =>
			toComparableTimeOffInstant(timeOff.startAt) === start &&
			toComparableTimeOffInstant(timeOff.endAt) === end
	);
	const exactId = parseUserTimeOffId(exactMatch);
	if (exactId != null) return exactId;

	const startDate = moment(startAt).format('YYYY-MM-DD');
	const endDate = moment(endAt).format('YYYY-MM-DD');
	const dateMatches = userTimeOffs.filter((timeOff) => {
		if (!timeOff.startAt || !timeOff.endAt) return false;
		return (
			moment(timeOff.startAt).format('YYYY-MM-DD') === startDate &&
			moment(timeOff.endAt).format('YYYY-MM-DD') === endDate
		);
	});

	return dateMatches.length === 1 ? parseUserTimeOffId(dateMatches[0]) : null;
};

export const getLiveUserTimeOffId = ({
	requestUserTimeOff,
	employeeTimeOffs,
	startAt,
	endAt,
}: {
	requestUserTimeOff?: UserTimeOffMatch | string | number | null;
	employeeTimeOffs?: UserTimeOffMatch[];
	startAt?: string | Date | null;
	endAt?: string | Date | null;
}): number | null => {
	const matchedId = findMatchingUserTimeOffId(employeeTimeOffs, startAt, endAt);
	if (matchedId != null) return matchedId;

	const requestedId = parseUserTimeOffId(requestUserTimeOff);
	if (requestedId == null) return null;

	const stillLive = (employeeTimeOffs || []).some(
		(timeOff) => parseUserTimeOffId(timeOff) === requestedId
	);
	return stillLive ? requestedId : null;
};

export const shouldDisplayCalendarTimeOff = ({
	userTimeOffId,
	removedUserTimeOffIds,
}: {
	userTimeOffId?: number | null;
	removedUserTimeOffIds?: Iterable<number>;
}): boolean => {
	if (userTimeOffId == null) return false;
	if (!removedUserTimeOffIds) return true;
	const removed =
		removedUserTimeOffIds instanceof Set
			? removedUserTimeOffIds
			: new Set(removedUserTimeOffIds);
	return !removed.has(userTimeOffId);
};

export const removeUserTimeOffFromUsers = <T extends { userTimeOff?: UserTimeOffMatch[] }>(
	users: T[] | undefined,
	userTimeOffId: number
): T[] | undefined => {
	if (!users) return users;
	return users.map((user) => ({
		...user,
		userTimeOff: (user.userTimeOff || []).filter(
			(timeOff) => parseUserTimeOffId(timeOff) !== userTimeOffId
		),
	}));
};

export const getUserTimeOffIdFromEvent = (
	event?: unknown,
	employeeTimeOffs?: UserTimeOffMatch[]
): number | null => {
	const timeOffEvent = asTimeOffCalendarEvent(event);
	const fromEvent = parseUserTimeOffId(timeOffEvent?.userTimeOffId);
	if (fromEvent != null) return fromEvent;

	const request = timeOffEvent?.timeOffRequest;
	const fromRequest = parseUserTimeOffId(request?.userTimeOff);
	if (fromRequest != null) return fromRequest;

	return findMatchingUserTimeOffId(
		employeeTimeOffs,
		timeOffEvent?.startAt ?? request?.startAt,
		timeOffEvent?.endAt ?? request?.endAt
	);
};

export const getTimeOffOwnerId = (event?: unknown): string | null => {
	const timeOffEvent = asTimeOffCalendarEvent(event);
	const ownerId =
		timeOffEvent?.timeOffRequest?.employeeRequest?.id ?? timeOffEvent?.employee?.id ?? null;
	return ownerId == null ? null : String(ownerId);
};

export const isCalendarTimeOffOwner = (
	event?: unknown,
	currentUserId?: string | number | null
): boolean => {
	if (currentUserId == null || currentUserId === '') return false;
	const ownerId = getTimeOffOwnerId(event);
	return ownerId != null && ownerId === String(currentUserId);
};

export const canViewCalendarTimeOff = ({
	event,
	currentUserId,
	canViewAllSchedules,
}: {
	event?: unknown;
	currentUserId?: string | number | null;
	canViewAllSchedules?: boolean;
}): boolean => Boolean(canViewAllSchedules) || isCalendarTimeOffOwner(event, currentUserId);

export const canDeleteCalendarTimeOff = ({
	event,
	currentUserId,
	canDeleteShifts,
	canManageAllRequests,
}: {
	event?: unknown;
	currentUserId?: string | number | null;
	canDeleteShifts?: boolean;
	canManageAllRequests?: boolean;
}): boolean =>
	Boolean(canDeleteShifts) ||
	Boolean(canManageAllRequests) ||
	isCalendarTimeOffOwner(event, currentUserId);

export const getTimeOffApproverName = (
	request?: TimeOffCalendarRequest | null
): string => {
	const namedApprover =
		formatTimeOffPersonName(request?.approvedBy) ||
		formatTimeOffPersonName(request?.updatedBy);
	if (namedApprover) return namedApprover;

	const requesterId = request?.employeeRequest?.id;
	const creatorId = request?.createdBy?.id;
	if (
		creatorId != null &&
		requesterId != null &&
		String(creatorId) !== String(requesterId)
	) {
		return formatTimeOffPersonName(request?.createdBy);
	}

	return '';
};

export const getTimeOffDeleteDialogCopy = (
	isOwner: boolean
): { title: string; message: string } => {
	if (isOwner) {
		return {
			title: 'Delete Time Off',
			message:
				'This will delete your time off request. You will need to submit a new Time Off Request if you still need this time off.',
		};
	}

	return {
		title: 'Delete Time Off',
		message:
			'This will remove this approved time off from the schedule. The employee will need to submit a new Time Off Request if time off is still needed.',
	};
};
